import { useMemo } from 'react';
import { useRegistration } from '@/context/RegistrationContext';
import {
  VIEW_GATING_ECKM,
  VIEW_GATING_CKM,
  VIEW_GATING_BH,
  CONTINUE_WEBREG,
} from '@/consts/events';

export type ConditionStage = 'eCKM' | 'CKM' | 'BH';

interface ConditionConfig {
  key: string;
  translationKey: string;
}

export interface StageConfig {
  stage: ConditionStage;
  dataKey: 'eCKMConditions' | 'ckmConditions' | 'behavioralHealthConditions';
  updateFn: (data: any) => void; // Can be ECKMConditionsData | CKMConditionsData | BehavioralHealthConditionsData
  getNextRoute: () => string;
  screenViewEvent: string;
  buttonPressedEvent: string;
  translationNamespace: string;
  /**
   * eCKM folds the subtitle into the heading to match Boris's single-heading
   * design; set false to hide the separate subtitle line. CKM/BH keep theirs.
   */
  showSubtitle?: boolean;
  conditions: ConditionConfig[];
}

/**
 * Custom hook to get stage configuration for health conditions flow
 * Encapsulates all stage-specific logic, events, and navigation
 */
export const useHealthConditionsStage = (stage: ConditionStage): StageConfig => {
  const {
    updateECKMConditions,
    updateCKMConditions,
    updateBehavioralHealthConditions,
    getNextRouteAfterECKM,
    getNextRouteAfterCKM,
  } = useRegistration();

  const stageConfig = useMemo((): StageConfig => {
    switch (stage) {
      case 'eCKM':
        return {
          stage: 'eCKM',
          dataKey: 'eCKMConditions',
          updateFn: updateECKMConditions,
          getNextRoute: getNextRouteAfterECKM,
          screenViewEvent: VIEW_GATING_ECKM,
          buttonPressedEvent: CONTINUE_WEBREG,
          translationNamespace: 'eckm-conditions',
          showSubtitle: false,
          conditions: [
            {
              key: 'highBloodPressure',
              translationKey: 'high-blood-pressure',
            },
            {
              key: 'highCholesterol',
              translationKey: 'high-cholesterol',
            },
            {
              key: 'prediabetes',
              translationKey: 'prediabetes',
            },
            {
              key: 'overweightOrObesity',
              translationKey: 'overweight-or-obesity',
            },
          ],
        };

      case 'CKM':
        return {
          stage: 'CKM',
          dataKey: 'ckmConditions',
          updateFn: updateCKMConditions,
          getNextRoute: getNextRouteAfterCKM,
          screenViewEvent: VIEW_GATING_CKM,
          buttonPressedEvent: CONTINUE_WEBREG,
          translationNamespace: 'ckm-conditions',
          conditions: [
            {
              key: 'diabetes',
              translationKey: 'diabetes',
            },
            {
              key: 'chronicKidneyDisease',
              translationKey: 'chronic-kidney-disease',
            },
            {
              key: 'heartDisease',
              translationKey: 'heart-disease',
            },
          ],
        };

      case 'BH':
        return {
          stage: 'BH',
          dataKey: 'behavioralHealthConditions',
          updateFn: updateBehavioralHealthConditions,
          // BH-continue routing is owned by useEligibilitySubmission (RND-33880):
          // it is the single place that derives candidate tracks (via
          // determineEligibility), fires the per-track CMS submissions, and
          // returns the next route. getNextRoute must never be called for BH.
          getNextRoute: () => {
            throw new Error('BH routing is handled by useEligibilitySubmission (RND-33880)');
          },
          screenViewEvent: VIEW_GATING_BH,
          buttonPressedEvent: CONTINUE_WEBREG,
          translationNamespace: 'behavioral-health-conditions',
          conditions: [
            {
              key: 'depression',
              translationKey: 'depression',
            },
            {
              key: 'anxiety',
              translationKey: 'anxiety',
            },
          ],
        };
    }
  }, [
    stage,
    updateECKMConditions,
    updateCKMConditions,
    updateBehavioralHealthConditions,
    getNextRouteAfterECKM,
    getNextRouteAfterCKM,
  ]);

  return stageConfig;
};
